import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { LinkableTextarea } from '@/components/admin/LinkableTextarea';
import axios from 'axios';

const slugify = (title) =>
  (title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Exercise, Check-In and Module Reflection share one upload form; only the wording differs.
const DOC_LABEL = {
  exercise: 'Exercise',
  check_in: 'Check-In',
  reflection: 'Module Reflection',
};

export const AdminContentTab = ({
  contents,
  contentForm,
  setContentForm,
  editingContent,
  contentFormRef,
  modules,
  folders,
  photoOptions,
  selectedPhoto,
  setSelectedPhoto,
  loadingPhotos,
  showUnsplashModal,
  setShowUnsplashModal,
  unsplashSearchQuery,
  setUnsplashSearchQuery,
  unsplashResults,
  expandedModules,
  setExpandedModules,
  expandedFolders,
  setExpandedFolders,
  viewPhotoModal,
  setViewPhotoModal,
  selectedPhotoUrl,
  setSelectedPhotoUrl,
  selectedPhotoAttribution,
  setSelectedPhotoAttribution,
  createContent,
  fetchPhotoOptions,
  searchUnsplash,
  openUnsplashBrowser,
  selectPhotoFromModal,
  handleDeleteContent,
  handleEditContent,
  resetContentForm,
  toggleModuleExpansion
}) => {
  const getContentsByModule = (moduleId) => contents.filter(c => c.module_id === moduleId).sort((a, b) => a.order - b.order);

  return (
    <>
      {contents.filter(c => !c.folder_id).length > 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="font-semibold text-amber-800 mb-2">📁 Folder System Active</h3>
              <p className="text-sm text-amber-700 mb-2">
                You have <strong>{contents.filter(c => !c.folder_id).length} unassigned content items</strong> from before the folder system.
              </p>
              <p className="text-sm text-amber-700">
                <strong>To organize them:</strong> Scroll down to "Unassigned Content" section, click "Assign to Folder" on each item, select a folder, and save.
              </p>
            </div>
          )}
          
          {/* Remounted when he moves to another item, so no uncontrolled input (least of all a
              file picker) can carry the last thing over */}
          <Card ref={contentFormRef} key={editingContent || 'new'}>
            <CardHeader>
              <CardTitle>
                {editingContent ? 'Edit Module Content' : 'Create Module Content'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Folder * (module is auto-set from folder)</Label>
                <Select 
                  value={contentForm.folder_id} 
                  onValueChange={(value) => {
                    const selectedFolder = folders.find(f => f.id === value);
                    setContentForm({ 
                      ...contentForm, 
                      folder_id: value,
                      module_id: selectedFolder?.module_id || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Folder" />
                  </SelectTrigger>
                  <SelectContent>
                    {modules.map((module) => {
                      const moduleFolders = folders
                        .filter(f => f.module_id === module.id)
                        .sort((a, b) => a.order - b.order);
                      
                      if (moduleFolders.length === 0) return null;
                      
                      return (
                        <div key={module.id}>
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100">
                            {module.title}
                          </div>
                          {moduleFolders.map((folder) => (
                            <SelectItem key={folder.id} value={folder.id}>
                              &nbsp;&nbsp;{folder.order}. {folder.name}
                            </SelectItem>
                          ))}
                        </div>
                      );
                    })}
                  </SelectContent>
                </Select>
                {folders.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    No folders created yet. Create folders first in the Folders tab.
                  </p>
                )}
              </div>
              <div>
                <Label>Content Title</Label>
                <Input
                  value={contentForm.title}
                  onChange={(e) => setContentForm({
                    ...contentForm,
                    title: e.target.value,
                    // Public blogs keep their URL in step with the title
                    slug: contentForm.is_public ? slugify(e.target.value) : contentForm.slug
                  })}
                  placeholder="Content Title"
                />
              </div>
              <div>
                <Label>Content Type</Label>
                <Select 
                  value={contentForm.content_type} 
                  onValueChange={(value) => setContentForm({ ...contentForm, content_type: value, content_data: {} })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="meditation">Meditation/Audio</SelectItem>
                    <SelectItem value="blog">Blog/Article</SelectItem>
                    <SelectItem value="blog_audio">Additional Audio</SelectItem>
                    <SelectItem value="exercise">Exercise</SelectItem>
                    <SelectItem value="supplement">Supplement</SelectItem>
                    <SelectItem value="check_in">Check-In</SelectItem>
                    <SelectItem value="reflection">Reflections</SelectItem>
                    <SelectItem value="link">External Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Video Fields */}
              {contentForm.content_type === 'video' && (
                <>
                  <div>
                    <Label>Video Source</Label>
                    <Select
                      value={contentForm.content_data?.video_source || 'url'}
                      onValueChange={(value) => setContentForm({
                        ...contentForm,
                        content_data: { ...contentForm.content_data, video_source: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="url">YouTube/Vimeo URL</SelectItem>
                        <SelectItem value="upload">Upload MP4 File</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* URL Option */}
                  {(!contentForm.content_data?.video_source || contentForm.content_data?.video_source === 'url') && (
                    <div>
                      <Label>Video URL (YouTube, Vimeo, etc.)</Label>
                      <Input
                        value={contentForm.content_data?.video_url || ''}
                        placeholder="https://www.youtube.com/embed/..."
                        onChange={(e) => setContentForm({ 
                          ...contentForm, 
                          content_data: { ...contentForm.content_data, video_url: e.target.value }
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Paste the embed URL from YouTube or Vimeo</p>
                    </div>
                  )}

                  {/* File Upload Option */}
                  {contentForm.content_data?.video_source === 'upload' && (
                    <>
                      <div>
                        <Label>Upload Video (MP4, MOV, AVI, WebM - Max 1GB)</Label>
                        <Input
                          type="file"
                          accept=".mp4,.mov,.avi,.webm"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            // Clear the picker so the old name is not left sitting there,
                            // and so the same file can be chosen again if needed
                            e.target.value = '';
                            if (!file) return;

                            // Check size
                            if (file.size > 1000 * 1024 * 1024) {
                              toast.error('Video file too large. Maximum size is 1GB');
                              e.target.value = '';
                              return;
                            }

                            // Upload with progress
                            const formData = new FormData();
                            formData.append('file', file);

                            try {
                              toast.info(`Uploading ${file.name}... (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
                              
                              const response = await axios.post(`${API}/upload-video`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });

                              if (response.data.success) {
                                setContentForm({
                                  ...contentForm,
                                  content_data: {
                                    ...contentForm.content_data,
                                    video_file_url: response.data.file_url,
                                    video_filename: response.data.filename
                                  }
                                });
                                toast.success('Video uploaded successfully!');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Video upload failed');
                              e.target.value = '';
                            }
                          }}
                        />
                        {contentForm.content_data?.video_filename && (
                          <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.video_filename}</p>
                        )}
                      </div>

                      <div>
                        <Label>Upload Thumbnail (JPG, PNG - Max 5MB)</Label>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            // Clear the picker so the old name is not left sitting there,
                            // and so the same file can be chosen again if needed
                            e.target.value = '';
                            if (!file) return;

                            // Check size
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Thumbnail too large. Maximum size is 5MB');
                              e.target.value = '';
                              return;
                            }

                            // Upload thumbnail
                            const formData = new FormData();
                            formData.append('file', file);

                            try {
                              const response = await axios.post(`${API}/upload-thumbnail`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });

                              if (response.data.success) {
                                setContentForm({
                                  ...contentForm,
                                  content_data: {
                                    ...contentForm.content_data,
                                    thumbnail_url: response.data.file_url,
                                    thumbnail_filename: response.data.filename
                                  }
                                });
                                toast.success('Thumbnail uploaded successfully!');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Thumbnail upload failed');
                              e.target.value = '';
                            }
                          }}
                        />
                        {contentForm.content_data?.thumbnail_url && (
                          <div className="mt-2">
                            <img 
                              src={contentForm.content_data.thumbnail_url} 
                              alt="Thumbnail preview" 
                              className="w-32 h-20 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={contentForm.content_data?.description || ''}
                      placeholder="Describe this video..."
                      rows={3}
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, description: e.target.value }
                      })}
                    />
                  </div>
                </>
              )}

              {/* Meditation/Audio Fields */}
              {contentForm.content_type === 'meditation' && (
                <>
                  <div>
                    <Label>Upload Audio/Video File (MP3, MP4, WAV, M4A, AAC)</Label>
                    <Input
                      type="file"
                      accept=".mp3,.mp4,.wav,.m4a,.aac"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        // Clear the picker so the old name is not left sitting there,
                        // and so the same file can be chosen again if needed
                        e.target.value = '';
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-meditation`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setContentForm({ 
                              ...contentForm, 
                              content_data: { 
                                ...contentForm.content_data, 
                                audio_url: response.data.file_path,
                                filename: response.data.filename
                              }
                            });
                            toast.success('File uploaded successfully!');
                          } catch (error) {
                            toast.error('File upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload your meditation audio or video file</p>
                    {contentForm.content_data?.filename && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.filename}</p>
                    )}
                  </div>
                  <div>
                    <Label>Or paste external URL (optional)</Label>
                    <Input
                      value={contentForm.content_data?.audio_url || ''}
                      placeholder="https://..."
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, audio_url: e.target.value }
                      })}
                    />
                  </div>
                  <LinkableTextarea
                    label="Instructions/Text (optional)"
                    value={contentForm.content_data?.instructions || ''}
                    placeholder="Paste your meditation instructions or content here. You can copy and paste from Word."
                    rows={8}
                    hint="Copy and paste directly from Word — and link straight to a chat, module or section."
                    testid="content-meditation-instructions"
                    onChange={(v) => setContentForm({
                      ...contentForm,
                      content_data: { ...contentForm.content_data, instructions: v },
                    })}
                  />
                </>
              )}

              {/* Blog/Audio Fields */}
              {contentForm.content_type === 'blog_audio' && (
                <>
                  <div>
                    <Label>Upload Audio/Video File (MP3, MP4, WAV, M4A, AAC)</Label>
                    <Input
                      type="file"
                      accept=".mp3,.mp4,.wav,.m4a,.aac"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        // Clear the picker so the old name is not left sitting there,
                        // and so the same file can be chosen again if needed
                        e.target.value = '';
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-meditation`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setContentForm({ 
                              ...contentForm, 
                              content_data: { 
                                ...contentForm.content_data, 
                                audio_url: response.data.file_path,
                                filename: response.data.filename
                              }
                            });
                            toast.success('Audio file uploaded successfully!');
                          } catch (error) {
                            toast.error('File upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload your blog audio file (voice narration, podcast episode, etc.)</p>
                    {contentForm.content_data?.filename && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.filename}</p>
                    )}
                  </div>
                  <div>
                    <Label>Or paste external URL (optional)</Label>
                    <Input
                      value={contentForm.content_data?.audio_url || ''}
                      placeholder="https://..."
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, audio_url: e.target.value }
                      })}
                    />
                  </div>
                  <LinkableTextarea
                    label="Show Notes/Transcript (optional)"
                    value={contentForm.content_data?.instructions || ''}
                    placeholder="Add show notes, transcript, or additional text content..."
                    rows={6}
                    hint="Optional: add show notes or a transcript, with links into the platform."
                    testid="content-audio-instructions"
                    onChange={(v) => setContentForm({
                      ...contentForm,
                      content_data: { ...contentForm.content_data, instructions: v },
                    })}
                  />
                </>
              )}

              {/* Blog Fields */}
              {contentForm.content_type === 'blog' && (
                <>
                  <div>
                    <Label>Upload Blog Document (DOCX, PDF)</Label>
                    <Input
                      type="file"
                      accept=".docx,.doc,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        // Clear the picker so the old name is not left sitting there,
                        // and so the same file can be chosen again if needed
                        e.target.value = '';
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-document`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setContentForm({ 
                              ...contentForm, 
                              content_data: { 
                                ...contentForm.content_data,
                                document_url: response.data.file_url,
                                pdf_url: response.data.file_url, // Use same URL for PDF
                                filename: response.data.filename
                              }
                            });
                            toast.success('Document uploaded successfully!');
                          } catch (error) {
                            toast.error('Document upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload DOCX or PDF - will be viewable and printable in browser</p>
                    {contentForm.content_data?.filename && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.filename}</p>
                    )}
                  </div>
                  <div>
                    <Label>Blog Summary (optional)</Label>
                    <Textarea
                      value={contentForm.content_data?.summary || ''}
                      placeholder="Add a brief summary or introduction..."
                      rows={4}
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, summary: e.target.value }
                      })}
                    />
                  </div>

                  {/* Add Audio Checkbox */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="add-blog-audio"
                      checked={contentForm.content_data?.has_audio || false}
                      onChange={(e) => setContentForm({
                        ...contentForm,
                        content_data: { 
                          ...contentForm.content_data, 
                          has_audio: e.target.checked 
                        }
                      })}
                      className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
                    />
                    <Label htmlFor="add-blog-audio" className="cursor-pointer">Add Audio</Label>
                  </div>

                  {/* Blog/Audio Upload Section - Shows when checkbox is checked */}
                  {contentForm.content_data?.has_audio && (
                    <div className="border-l-4 border-cyan-400 pl-4 space-y-4">
                      <h4 className="font-semibold text-gray-900">Blog/Audio</h4>
                      <div>
                        <Label>Upload Audio/Video File (MP3, MP4, WAV, M4A, AAC)</Label>
                        <Input
                          type="file"
                          accept=".mp3,.mp4,.wav,.m4a,.aac"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            // Clear the picker so the old name is not left sitting there,
                            // and so the same file can be chosen again if needed
                            e.target.value = '';
                            if (file) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                const response = await axios.post(`${API}/upload-meditation`, formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                setContentForm({ 
                                  ...contentForm, 
                                  content_data: { 
                                    ...contentForm.content_data, 
                                    blog_audio_url: response.data.file_path,
                                    blog_audio_filename: response.data.filename
                                  }
                                });
                                toast.success('Audio file uploaded successfully!');
                              } catch (error) {
                                toast.error('Audio upload failed');
                              }
                            }
                          }}
                          className="cursor-pointer"
                        />
                        <p className="text-xs text-gray-500 mt-1">Upload audio narration for this blog article</p>
                        {contentForm.content_data?.blog_audio_filename && (
                          <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.blog_audio_filename}</p>
                        )}
                      </div>
                      <div>
                        <Label>Or paste external URL (optional)</Label>
                        <Input
                          value={contentForm.content_data?.blog_audio_url || ''}
                          placeholder="https://..."
                          onChange={(e) => setContentForm({ 
                            ...contentForm, 
                            content_data: { ...contentForm.content_data, blog_audio_url: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Public Blog Controls */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Label className="text-base font-semibold">Make Public (Hidden Link)</Label>
                        <p className="text-xs text-gray-500 mt-1">Allow access via direct URL without login</p>
                      </div>
                      <Switch
                        checked={contentForm.is_public || false}
                        onCheckedChange={(checked) => setContentForm({ 
                          ...contentForm, 
                          is_public: checked,
                          slug: checked ? slugify(contentForm.title) : contentForm.slug
                        })}
                        data-testid="content-make-public-toggle"
                      />
                    </div>
                    
                    {contentForm.is_public && (
                      <>
                        <div className="mb-4">
                          <Label>URL Slug</Label>
                          <Input
                            value={contentForm.slug || ''}
                            placeholder="my-blog-post"
                            data-testid="content-slug-input"
                            onChange={(e) => setContentForm({ 
                              ...contentForm, 
                              slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                            })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Only letters, numbers, and hyphens allowed</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <Label className="text-sm font-semibold text-blue-900 mb-2 block">🌐 Public URL</Label>
                          <div className="bg-white border border-blue-300 rounded px-3 py-2 mb-2 font-mono text-sm text-blue-700 break-all">
                            {`${window.location.origin}/blog/${contentForm.slug || 'your-blog-slug'}`}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const url = `${window.location.origin}/blog/${contentForm.slug}`;
                              // Fallback copy method for better browser compatibility
                              const textArea = document.createElement('textarea');
                              textArea.value = url;
                              textArea.style.position = 'fixed';
                              textArea.style.left = '-999999px';
                              document.body.appendChild(textArea);
                              textArea.select();
                              try {
                                document.execCommand('copy');
                                toast.success('URL copied to clipboard!');
                              } catch (err) {
                                toast.error('Failed to copy URL. Please copy manually.');
                              }
                              document.body.removeChild(textArea);
                            }}
                            disabled={!contentForm.slug}
                            className="w-full"
                          >
                            📋 Copy URL
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Exercise Fields */}
              {/* Exercise, Check-In and Module Reflection all upload a document and convert
                  into a fillable worksheet, so they share the one form */}
              {['exercise', 'check_in', 'reflection'].includes(contentForm.content_type) && (
                <>
                  <div>
                    <Label>
                      Upload {DOC_LABEL[contentForm.content_type]} Document (DOCX, PDF)
                    </Label>
                    <Input
                      type="file"
                      accept=".docx,.doc,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        // Clear the picker so the old name is not left sitting there,
                        // and so the same file can be chosen again if needed
                        e.target.value = '';
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-document`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setContentForm({ 
                              ...contentForm, 
                              content_data: { 
                                ...contentForm.content_data,
                                document_url: response.data.file_url,
                                pdf_url: response.data.file_url, // Use same URL for PDF
                                filename: response.data.filename
                              }
                            });
                            toast.success('Document uploaded successfully!');
                          } catch (error) {
                            toast.error('Document upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload DOCX or PDF - will be viewable and printable in browser</p>
                    {contentForm.content_data?.filename && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.filename}</p>
                    )}
                  </div>
                  <LinkableTextarea
                    label={`${DOC_LABEL[contentForm.content_type]} Instructions (optional)`}
                    value={contentForm.content_data?.instructions || ''}
                    placeholder="Add any additional instructions here..."
                    rows={6}
                    hint="Point them at a chat, a section or anywhere else in the platform."
                    testid="content-exercise-instructions"
                    onChange={(v) => setContentForm({
                      ...contentForm,
                      content_data: { ...contentForm.content_data, instructions: v },
                    })}
                  />
                </>
              )}

              {/* Supplement Fields */}
              {contentForm.content_type === 'supplement' && (
                <>
                  <div>
                    <Label>Upload Supplement Document (DOCX, PDF)</Label>
                    <Input
                      type="file"
                      accept=".docx,.doc,.pdf,.xlsx,.xls,.pptx,.ppt"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        // Clear the picker so the old name is not left sitting there,
                        // and so the same file can be chosen again if needed
                        e.target.value = '';
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-document`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setContentForm({ 
                              ...contentForm, 
                              content_data: { 
                                ...contentForm.content_data,
                                document_url: response.data.file_url,
                                pdf_url: response.data.file_url, // Use same URL for PDF
                                filename: response.data.filename
                              }
                            });
                            toast.success('Document uploaded successfully!');
                          } catch (error) {
                            toast.error('Document upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload documents - will be viewable and printable in browser</p>
                    {contentForm.content_data?.filename && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded: {contentForm.content_data.filename}</p>
                    )}
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={contentForm.content_data?.description || ''}
                      placeholder="Add a description of this supplement..."
                      rows={4}
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, description: e.target.value }
                      })}
                    />
                  </div>
                </>
              )}

              {/* External Link Fields */}
              {contentForm.content_type === 'link' && (
                <>
                  <div>
                    <Label>External URL</Label>
                    <Input
                      value={contentForm.content_data?.url || ''}
                      placeholder="https://..."
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, url: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={contentForm.content_data?.description || ''}
                      placeholder="Describe what this link is about..."
                      rows={3}
                      onChange={(e) => setContentForm({ 
                        ...contentForm, 
                        content_data: { ...contentForm.content_data, description: e.target.value }
                      })}
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Order (within folder)</Label>
                <Input
                  type="number"
                  min="1"
                  value={contentForm.order}
                  onChange={(e) => setContentForm({ ...contentForm, order: parseInt(e.target.value) })}
                  placeholder="1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Order of this content within its folder
                </p>
              </div>
              
              {/* Photo Selection from Unsplash */}
              <div className="space-y-3">
                <Label>Select Photo from Unsplash</Label>
                
                {/* Selected photo preview */}
                {selectedPhoto && (
                  <div className="border-2 border-cyan-500 rounded-lg overflow-hidden">
                    <img 
                      src={selectedPhoto.url}
                      alt={selectedPhoto.description}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-2 bg-cyan-50 flex items-center justify-between">
                      <p className="text-xs text-gray-700">
                        by <span className="font-medium">{selectedPhoto.photographer}</span>
                      </p>
                      <Button
                        type="button"
                        onClick={() => setSelectedPhoto(null)}
                        size="sm"
                        variant="ghost"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Browse Unsplash button */}
                <Button
                  type="button"
                  onClick={openUnsplashBrowser}
                  className="w-full"
                  variant="outline"
                >
                  {selectedPhoto ? 'Change Photo' : 'Browse Unsplash Photos'}
                </Button>
              </div>
              
              <Button onClick={createContent} className="btn-primary w-full">
                {editingContent ? 'Update Content' : 'Create Content'}
              </Button>
              {editingContent && (
                <Button onClick={resetContentForm} variant="outline" className="w-full">
                  Cancel Edit
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Unassigned Content - Content without folders */}
          {contents.filter(c => !c.folder_id).length > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-amber-800">⚠️ Unassigned Content (No Folder)</CardTitle>
                <p className="text-sm text-amber-700">
                  This content was created before the folder system. Edit each item to assign it to a folder.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {contents
                    .filter(c => !c.folder_id)
                    .sort((a, b) => a.order - b.order)
                    .map((content) => {
                      const module = modules.find(m => m.id === content.module_id);
                      return (
                        <div key={content.id} className="border rounded p-3 bg-white">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{content.title}</span>
                                <span className="text-xs px-2 py-1 bg-cyan-100 text-cyan-800 rounded">
                                  {content.content_type}
                                </span>
                                <span className="text-xs text-gray-500">Order: {content.order}</span>
                                {module && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                    Module: {module.title}
                                  </span>
                                )}
                                {/* Photo Indicator */}
                                {(content.content_data?.ai_image || content.ai_image) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedPhotoUrl(content.content_data?.ai_image || content.ai_image);
                                      setSelectedPhotoAttribution(content.content_data?.image_attribution);
                                      setViewPhotoModal(true);
                                    }}
                                    className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                    title="View photo"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Photo
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditContent(content, contentFormRef)}
                                className="flex items-center gap-1"
                                data-testid={`assign-content-${content.id}`}
                              >
                                <Edit className="w-3 h-3" />
                                Assign to Folder
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteContent(content.id)}
                                className="flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Existing Module Content - Organized by Folders */}
          <Card>
            <CardHeader>
              <CardTitle>Content Organized by Folders</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {modules.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No modules created yet</p>
              ) : (
                modules.map((module) => {
                  const moduleFolders = folders.filter(f => f.module_id === module.id).sort((a, b) => a.order - b.order);
                  const moduleContents = getContentsByModule(module.id);
                  const isExpanded = expandedModules[module.id];
                  
                  return (
                    <div key={module.id} className="border rounded-lg">
                      <button
                        onClick={() => toggleModuleExpansion(module.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-semibold">📚 {module.title}</span>
                          <span className="text-sm text-gray-500">({moduleFolders.length} folders, {moduleContents.length} items)</span>
                        </div>
                        <span className="text-xl">{isExpanded ? '▼' : '▶'}</span>
                      </button>
                      
                      {isExpanded && (
                        <div className="border-t p-4 space-y-4 bg-gray-50">
                          {moduleFolders.length === 0 ? (
                            <p className="text-gray-500 text-sm">No folders in this module yet. Create folders in the Folders tab first.</p>
                          ) : (
                            moduleFolders.map((folder) => {
                              const folderContents = contents.filter(c => c.folder_id === folder.id).sort((a, b) => a.order - b.order);
                              const isFolderExpanded = expandedModules[folder.id];
                              
                              return (
                                <div key={folder.id} className="border rounded-lg bg-white">
                                  <button
                                    onClick={() => setExpandedModules(prev => ({ ...prev, [folder.id]: !prev[folder.id] }))}
                                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">📁 {folder.order}. {folder.name}</span>
                                      {folder.days_delay > 0 && (
                                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                          ⏱️ {folder.days_delay} days delay
                                        </span>
                                      )}
                                      <span className="text-xs text-gray-500">({folderContents.length} items)</span>
                                    </div>
                                    <span>{isFolderExpanded ? '▼' : '▶'}</span>
                                  </button>
                                  
                                  {isFolderExpanded && (
                                    <div className="border-t p-3 space-y-2 bg-gray-50">
                                      {folderContents.length === 0 ? (
                                        <p className="text-gray-500 text-sm">No content in this folder yet</p>
                                      ) : (
                                        folderContents.map((content) => (
                                          <div key={content.id} className="border rounded p-3 bg-white">
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="font-medium">{content.title}</span>
                                                  <span className="text-xs px-2 py-1 bg-cyan-100 text-cyan-800 rounded">
                                                    {content.content_type}
                                                  </span>
                                                  <span className="text-xs text-gray-500">Order: {content.order}</span>
                                                  {content.is_public && (
                                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded flex items-center gap-1">
                                                      🌐 Public
                                                    </span>
                                                  )}
                                                  {/* Photo Indicator */}
                                                  {(content.content_data?.ai_image || content.ai_image) && (
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => {
                                                        setSelectedPhotoUrl(content.content_data?.ai_image || content.ai_image);
                                                        setSelectedPhotoAttribution(content.content_data?.image_attribution);
                                                        setViewPhotoModal(true);
                                                      }}
                                                      className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                                      title="View photo"
                                                    >
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                      </svg>
                                                      Photo
                                                    </Button>
                                                  )}
                                                </div>
                                                
                                                {/* Show public URL if available */}
                                                {content.is_public && content.slug && (
                                      <div className="mt-2 text-xs">
                                        <span className="text-gray-500">Public URL: </span>
                                        <a 
                                          href={`/blog/${content.slug}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline"
                                        >
                                          {window.location.origin}/blog/{content.slug}
                                        </a>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const url = `${window.location.origin}/blog/${content.slug}`;
                                            const textArea = document.createElement('textarea');
                                            textArea.value = url;
                                            textArea.style.position = 'fixed';
                                            textArea.style.left = '-999999px';
                                            document.body.appendChild(textArea);
                                            textArea.select();
                                            try {
                                              document.execCommand('copy');
                                              toast.success('URL copied!');
                                            } catch (err) {
                                              toast.error('Failed to copy');
                                            }
                                            document.body.removeChild(textArea);
                                          }}
                                          className="ml-2 text-blue-600 hover:text-blue-800"
                                        >
                                          📋
                                                  </button>
                                                </div>
                                                )}
                                                
                                                {/* Show content preview based on type */}
                                                <div className="text-sm text-gray-600 mt-2">
                                      {content.content_type === 'video' && content.content_data?.video_url && (
                                        <div>📹 Video: {content.content_data.video_url}</div>
                                      )}
                                      {content.content_type === 'meditation' && content.content_data?.file_url && (
                                        <div>🧘 Meditation file uploaded</div>
                                      )}
                                      {content.content_type === 'blog' && content.content_data?.text && (
                                        <div>📝 {content.content_data.text.substring(0, 100)}...</div>
                                      )}
                                      {['exercise', 'check_in', 'reflection'].includes(content.content_type) && content.content_data?.instructions && (
                                        <div>💪 {content.content_data.instructions.substring(0, 100)}...</div>
                                      )}
                                      {content.content_type === 'supplement' && content.content_data?.description && (
                                        <div>📚 {content.content_data.description.substring(0, 100)}...</div>
                                      )}
                                      {content.content_type === 'link' && content.content_data?.url && (
                                        <div>
                                          🔗 <a 
                                            href={content.content_data.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-cyan-600 hover:underline"
                                          >
                                            {content.content_data.url}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                                    
                                                    {/* Show content preview based on type */}
                                                    <div className="text-sm text-gray-600 mt-2">
                                                      {content.content_type === 'video' && content.content_data?.video_url && (
                                                        <div>📹 Video: {content.content_data.video_url}</div>
                                                      )}
                                                      {content.content_type === 'meditation' && content.content_data?.file_url && (
                                                        <div>🧘 Meditation file uploaded</div>
                                                      )}
                                                      {content.content_type === 'blog' && content.content_data?.text && (
                                                        <div>📝 {content.content_data.text.substring(0, 100)}...</div>
                                                      )}
                                                      {['exercise', 'check_in', 'reflection'].includes(content.content_type) && content.content_data?.instructions && (
                                                        <div>💪 {content.content_data.instructions.substring(0, 100)}...</div>
                                                      )}
                                                      {content.content_type === 'supplement' && content.content_data?.description && (
                                                        <div>📚 {content.content_data.description.substring(0, 100)}...</div>
                                                      )}
                                                      {content.content_type === 'link' && content.content_data?.url && (
                                                        <div>
                                                          🔗 <a 
                                                            href={content.content_data.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-cyan-600 hover:underline"
                                                          >
                                                            {content.content_data.url}
                                                          </a>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  
                                                  <div className="flex gap-2 ml-4">
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => handleEditContent(content, contentFormRef)}
                                                      className="flex items-center gap-1"
                                                      data-testid={`edit-content-${content.id}`}
                                                    >
                                                      <Edit className="w-3 h-3" />
                                                      Edit
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant="destructive"
                                                      onClick={() => handleDeleteContent(content.id)}
                                                      className="flex items-center gap-1"
                                                    >
                                                      <Trash2 className="w-3 h-3" />
                                                      Delete
                                                    </Button>
                                                  </div>
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
    </>
  );
};

export default AdminContentTab;
